import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";

const SCATTER = [
  { x: 25,   y: 18,  w: 7.5, h: 88 },
  { x: 39,   y: 15,  w: 6.5, h: 70 },
  { x: 55,   y: 18,  w: 6.0, h: 63 },
  { x: 74,   y: 15,  w: 7.0, h: 76 },
  { x: 26,   y: 48,  w: 7.0, h: 73 },
  { x: 39,   y: 44,  w: 6.2, h: 66 },
  { x: 57,   y: 47,  w: 7.5, h: 80 },
  { x: 78,   y: 45,  w: 6.2, h: 67 },
  { x: 20,   y: 75,  w: 7.0, h: 76 },
  { x: 36,   y: 77,  w: 6.5, h: 70 },
  { x: 54,   y: 73,  w: 7.5, h: 83 },
  { x: 68,   y: 65,  w: 6.2, h: 67 },
  { x: 78,   y: 76,  w: 6.2, h: 67 },
];

function buildCollage(mobile) {
  const GAP   = 1.2;
  const LEFT  = mobile ? 6.0  : 22.0;
  const TOTAL = mobile ? 88.0 : 56.0;
  const H     = mobile ? 80   : 120;

  const cw1 = (TOTAL - GAP * 4) / 5;
  const cw2 = (TOTAL - GAP * 4) / 5;
  const cw3 = cw1;

  const y1 = mobile ? 28 : 27;
  const y2 = mobile ? 43 : 46;
  const y3 = mobile ? 58 : 65;

  const rowOffset = (cw1 + GAP) / 2;

  const row = (n, cw, y, extraX = 0) =>
    Array.from({ length: n }, (_, i) => ({
      x: parseFloat((LEFT + extraX + i * (cw + GAP)).toFixed(2)),
      y,
      w: parseFloat(cw.toFixed(2)),
      h: H,
    }));

  return [
    ...row(4, cw1, y1, rowOffset),
    ...row(5, cw2, y2),
    ...row(4, cw3, y3, rowOffset),
  ];
}

export default function OurTeamSection({ registerIntent, url = "/" }) {

  const TEAM = [
    { id: 1,  name: "Zoha Habib",     role: "Associate Software Engineer", img: url + "images/new-home/team/1.png" },
    { id: 2,  name: "Muntaha Naveed",  role: "Digital Marketing Executive",     img: url + "images/new-home/team/2.png" },
    { id: 3,  name: "Umar Ashraf",       role: "Sr. Software Engineer",        img: url + "images/new-home/team/3.png" },
    { id: 4,  name: "Fatika Amir",    role: "Graphic Designer",        img: url + "images/new-home/team/4.png" },
    { id: 5,  name: "Ammar Arbab",    role: "Manager Data Services",           img: url + "images/new-home/team/5.png" },
    { id: 6,  name: "Mehboob Ur Rehman",       role: "Digital Marketing Manager",      img: url + "images/new-home/team/6.png" },
    { id: 7,  name: "Nailah Syed",  role: "Director",     img: url + "images/new-home/team/7.png" },
    { id: 8,  name: "Ziaullah Mughal", role: "Delivery Manager",       img: url + "images/new-home/team/8.png" },
    { id: 9,  name: "Mike Dawson",     role: "Content Writter",       img: url + "images/new-home/team/9.png"   },
    { id: 10, name: "Natalia Rodriguez", role: "Content Strategist",    img: url + "images/new-home/team/10.png" },
    { id: 11, name: "Abdul Rehman",    role: "Associate Software Engineer",       img: url + "images/new-home/team/11.png"   },
    { id: 12, name: "Jack Smith",      role: "QA Engineer",           img: url + "images/new-home/team/12.png" },
    { id: 13, name: "Ruman Malik",      role: "Associate Software Engineer",           img: url + "images/new-home/team/13.png"   },
  ];

  const [phase,   setPhase]   = useState(0);
  const [hovered, setHovered] = useState(null);
  const [winW,    setWinW]    = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  );

  const phaseRef      = useRef(0);
  const transitioning = useRef(false);
  const autoTimer     = useRef(null); // holds the auto-collage timeout

  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = winW < 992;
  const COLLAGE  = buildCollage(isMobile);

  const goPhase = useCallback((p) => {
    if (transitioning.current) return;
    if (p < 0 || p > 2) return;
    transitioning.current = true;
    phaseRef.current = p;
    setPhase(p);
    setTimeout(() => { transitioning.current = false; }, 1000);
  }, []);

  useEffect(() => {
    if (!registerIntent) return;
    registerIntent((dir) => {
      const next = phaseRef.current + dir;

      // Phase 0 → scroll down → go to phase 1 (scattered)
      // Phase 1 is AUTO — no scroll needed to reach phase 2
      // Phase 2 → scroll down → leave section (return false)
      // Scroll up from phase 2 → go back to phase 0 (intro)
      // Scroll up from phase 1 → go back to phase 0
      // Scroll up from phase 0 → leave section (return false)

      if (dir > 0) {
        if (phaseRef.current === 0) {
          goPhase(1);
          return true;
        }
        // Phase 1 or 2 — pass scroll to layout (leave section forward)
        return false;
      } else {
        if (phaseRef.current === 2 || phaseRef.current === 1) {
          // Clear auto timer if user scrolls back
          if (autoTimer.current) {
            clearTimeout(autoTimer.current);
            autoTimer.current = null;
          }
          goPhase(0);
          return true;
        }
        return false;
      }
    });
  }, [registerIntent, goPhase]);

  // ── Auto-animate: scattered → collage after 1.4s ─────────────────────
  // Triggered whenever phase becomes 1 (scattered)
  useEffect(() => {
    if (phase === 1) {
      // Wait for scatter animation to finish, then auto-assemble collage
      autoTimer.current = setTimeout(() => {
        goPhase(2);
        autoTimer.current = null;
      }, 1000); // 1.4s: scatter pop animations complete (~0.75s) + brief pause
    }

    return () => {
      if (autoTimer.current) {
        clearTimeout(autoTimer.current);
        autoTimer.current = null;
      }
    };
  }, [phase, goPhase]);

  const showCards = phase >= 1;

  const ruleVariants = {
    hidden: { scaleX: 0 },
    show:   { scaleX: 1, transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.2 } },
  };

  return (
    <section
      id="team-section"
      className={`ot ot--p${phase}`}
    >
      <div className="ot__noise" />

      {/* ══ PHASE 0: INTRO TEXT ══ */}
      <div className={`ot__intro ${phase === 0 ? "ot__intro--on" : "ot__intro--off"}`}>
        <motion.div className="ot__rule" variants={ruleVariants} />
        <h2 className="ot__intro-h">
          Meet The{" "}
          <em className="ot__grad">Creative<br />Minds</em>
          {" "}Behind Our<br />Success
        </h2>
        <p className="ot__intro-body">
          Scroll to meet the people who make it happen
        </p>
        <div className="ot__scroll-hint">
          <div className="ot__scroll-bar"><div className="ot__scroll-ball" /></div>
          <span>Scroll</span>
        </div>
      </div>

      {/* ══ PHASES 1+2: CARDS ══ */}
      <div className={`ot__stage ${showCards ? "ot__stage--on" : ""}`}>

        {TEAM.map((m, i) => {
          const s         = SCATTER[i];
          const c         = COLLAGE[i];
          const inCollage = phase === 2;
          const pos       = inCollage ? c : s;

          return (
            <div
              key={m.id}
              className={[
                "ot__card",
                phase === 1 ? "ot__card--scatter" : "",
                phase === 2 ? "ot__card--collage" : "",
              ].join(" ")}
              style={{
                left:            `${pos.x}%`,
                top:             `${pos.y}%`,
                width:           `${pos.w}%`,
                height:          `${pos.h}px`,
                transitionDelay: `${i * 0.05}s`,
                zIndex:          hovered === i ? 50 : i + 1,
              }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="ot__card-bg">
                <img src={m.img} alt={m.name} />
              </div>

              <div className="ot__card-overlay">
                <strong className="ot__card-name">{m.name}</strong>
                <span   className="ot__card-role">{m.role}</span>
              </div>

              <div className="ot__card-rim" />
            </div>
          );
        })}
      </div>
    </section>
  );
}

// import React, { useEffect, useRef, useState, useCallback } from "react";
// import { motion } from "framer-motion";




// const SCATTER = [
//   { x: 25,   y: 18,  w: 7.5, h: 88 },
//   { x: 39,   y: 15,  w: 6.5, h: 70 },
//   { x: 55,   y: 18,  w: 6.0, h: 63 },
//   { x: 74,   y: 15,  w: 7.0, h: 76 },
//   { x: 26,   y: 48,  w: 7.0, h: 73 },
//   { x: 39,   y: 44,  w: 6.2, h: 66 },
//   { x: 57,   y: 47,  w: 7.5, h: 80 },
//   { x: 78,   y: 45,  w: 6.2, h: 67 },
//   { x: 20,   y: 75,  w: 7.0, h: 76 },
//   { x: 36,   y: 77,  w: 6.5, h: 70 },
//   { x: 54,   y: 73,  w: 7.5, h: 83 },
//   { x: 68,   y: 65,  w: 6.2, h: 67 },
//   { x: 78,   y: 76,  w: 6.2, h: 67 },
// ];

// // ── Responsive collage builder ─────────────────────────────────────────────
// // Desktop (≥992px): 60% width centered, sidebar visible
// // Mobile  (<992px): full width with 2% padding each side, sidebar hidden
// function buildCollage(mobile) {
//   const GAP   = 1.2;
//   const LEFT  = mobile ? 6.0  : 22.0;
//   const TOTAL = mobile ? 88.0 : 56.0;
//   const H     = mobile ? 80   : 120;

//   const cw1 = (TOTAL - GAP * 4) / 5;  // row1: 4 cards
//   const cw2 = (TOTAL - GAP * 4) / 5;  // row2: 5 cards
//   const cw3 = cw1;                      // row3: 4 cards, same width as row1

//   const y1 = mobile ? 28  : 27;
//   const y2 = mobile ? 43 : 46;
//   const y3 = mobile ? 58 : 65;

//    const rowOffset = (cw1 + GAP) / 2;

//   const row = (n, cw, y, extraX = 0) =>
//     Array.from({ length: n }, (_, i) => ({
//       x: parseFloat((LEFT + extraX + i * (cw + GAP)).toFixed(2)),
//       y,
//       w: parseFloat(cw.toFixed(2)),
//       h: H,
//     }));

//   return [
//     ...row(4, cw1, y1, rowOffset),
//     ...row(5, cw2, y2),
//     ...row(4, cw3, y3, rowOffset),
//   ];
// }

// // phases: 0=intro, 1=scattered, 2=collage
// export default function OurTeamSection({ registerIntent, url = "/" }) {


//   const TEAM = [
//   { id: 1,  name: "Umar Ashraf",      role: "Sr. Software Engineer", img: url + "images/new-home/team/1.png" },
//   { id: 2,  name: "Sara Khan",       role: "Creative Director",  img: "https://randomuser.me/api/portraits/women/44.jpg" },
//   { id: 3,  name: "Usman Ali",       role: "Lead Developer",     img: "https://randomuser.me/api/portraits/men/32.jpg"   },
//   { id: 4,  name: "Fatima Malik",    role: "UI/UX Designer",     img: "https://randomuser.me/api/portraits/women/68.jpg" },
//   { id: 5,  name: "Hassan Tariq",    role: "AI Engineer",        img: "https://randomuser.me/api/portraits/men/45.jpg"   },
//   { id: 6,  name: "Zara Noor",       role: "Brand Strategist",   img: url + "images/new-home/team/2.png" },
//   { id: 7,  name: "Bilal Chaudhry",  role: "Backend Developer",  img: "https://randomuser.me/api/portraits/men/57.jpg"   },
//   { id: 8,  name: "Ayesha Siddiqui", role: "Project Manager",    img: "https://randomuser.me/api/portraits/women/35.jpg" },
//   { id: 9,  name: "Omar Sheikh",     role: "Motion Designer",    img: "https://randomuser.me/api/portraits/men/23.jpg"   },
//   { id: 10, name: "Nadia Butt",      role: "Content Strategist", img: "https://randomuser.me/api/portraits/women/57.jpg" },
//   { id: 11, name: "Kamran Javed",    role: "DevOps Engineer",    img: url + "images/new-home/team/3.png"   },
//   { id: 12, name: "Sana Iqbal",      role: "QA Engineer",        img: "https://randomuser.me/api/portraits/women/12.jpg" },
//   { id: 13, name: "Ali Hassan",      role: "QA Engineer",        img: "https://randomuser.me/api/portraits/men/88.jpg"   },
// ];


//   const [phase,   setPhase]   = useState(0);
//   const [hovered, setHovered] = useState(null);
//   // ── ADDED: track window width for responsive collage positions ──────────
//   const [winW, setWinW] = useState(
//     typeof window !== "undefined" ? window.innerWidth : 1440
//   );
//   const phaseRef      = useRef(0);
//   const transitioning = useRef(false);

//   // ── ADDED: resize listener ──────────────────────────────────────────────
//   useEffect(() => {
//     const onResize = () => setWinW(window.innerWidth);
//     window.addEventListener("resize", onResize);
//     return () => window.removeEventListener("resize", onResize);
//   }, []);

//   const isMobile = winW < 992;
//   // ── ADDED: COLLAGE is now computed dynamically based on screen width ────
//   const COLLAGE = buildCollage(isMobile);

//   const goPhase = useCallback((p) => {
//     if (transitioning.current) return;
//     if (p < 0 || p > 2) return;
//     transitioning.current = true;
//     phaseRef.current = p;
//     setPhase(p);
//     setTimeout(() => { transitioning.current = false; }, 1000);
//   }, []);

//   useEffect(() => {
//     if (!registerIntent) return;
//     registerIntent((dir) => {
//       const next = phaseRef.current + dir;
//       if (next >= 0 && next <= 2) {
//         goPhase(next);
//         return true;
//       }
//       return false;
//     });
//   }, [registerIntent, goPhase]);

//   const showCards = phase >= 1;

//   const ruleVariants = {
//     hidden: { scaleX: 0 },
//     show:   { scaleX: 1, transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.2 } },
//     };

//   return (
//     <section
//       id="team-section"
//       className={`ot ot--p${phase}`}
//     >
//       <div className="ot__noise" />

//       {/* ══ PHASE 0: INTRO TEXT ══ */}
//       <div className={`ot__intro ${phase === 0 ? "ot__intro--on" : "ot__intro--off"}`}>
//         <motion.div className="ot__rule" variants={ruleVariants} />
//         {/* <span className="ot__intro-eyebrow">
//           <i className="ot__intro-pip" />
//           Our Team
//         </span> */}
//         <h2 className="ot__intro-h">
//           Meet The{" "}
//           <em className="ot__grad">Creative<br />Minds</em>
//           {" "}Behind Our<br />Success
//         </h2>
//         <p className="ot__intro-body">
//           Scroll to meet the people who make it happen
//         </p>
//         <div className="ot__scroll-hint">
//           <div className="ot__scroll-bar"><div className="ot__scroll-ball" /></div>
//           <span>Scroll</span>
//         </div>
//       </div>

//       {/* ══ PHASES 1+2: CARDS ══ */}
//       <div className={`ot__stage ${showCards ? "ot__stage--on" : ""}`}>

//         {/* Label top-left */}
//         {/* <p className="ot__stage-lbl">
//           {phase === 1 ? "Our Creative Team" : "Meet the Full Team"}
//         </p> */}

//         {TEAM.map((m, i) => {
//           const s  = SCATTER[i];
//           const c  = COLLAGE[i];
//           const inCollage = phase === 2;
//           const pos = inCollage ? c : s;

//           return (
//             <div
//               key={m.id}
//               className={[
//                 "ot__card",
//                 phase === 1 ? "ot__card--scatter" : "",
//                 phase === 2 ? "ot__card--collage" : "",
//               ].join(" ")}
//               style={{
//                 left:   `${pos.x}%`,
//                 top:    `${pos.y}%`,
//                 // both scatter and collage use %, so CSS can animate smoothly
//                 width:  `${pos.w}%`,
//                 height: `${pos.h}px`,
//                 transitionDelay: `${i * 0.05}s`,
//                 zIndex: hovered === i ? 50 : i + 1,
//               }}
//               onMouseEnter={() => setHovered(i)}
//               onMouseLeave={() => setHovered(null)}
//             >
//               {/* bg — orange gradient placeholder (replace img tag for real photos) */}
//               <div className="ot__card-bg">
//                 {/* <img src={`${url}images/team/${m.id}.jpg`} alt={m.name} /> */}
//                 <div className="ot__card-bg">
//                   <img src={m.img} alt={m.name} />
//                 </div>
//                 {/* <span className="ot__initials">
//                   {m.name.split(" ").map(w => w[0]).join("")}
//                 </span> */}
//               </div>

//               {/* hover overlay with name+role */}
//               <div className="ot__card-overlay">
//                 <strong className="ot__card-name">{m.name}</strong>
//                 <span  className="ot__card-role">{m.role}</span>
//               </div>

//               <div className="ot__card-rim" />
//             </div>
//           );
//         })}
//       </div>

//       {/* Phase step dots */}
//       {/* <div className="ot__dots">
//         {[0,1,2].map(p => (
//           <button
//             key={p}
//             className={`ot__dot ${p === phase ? "ot__dot--on" : ""}`}
//             onClick={() => goPhase(p)}
//           />
//         ))}
//       </div> */}
//     </section>
//   );
// }